import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import queryString from 'query-string';
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loading from '../../components/layouts/Loading';
import { AuthContext } from '../../contexts/AuthContext';

const AlertMessage = lazy(() =>
  import('../../components/layouts/AlertMessage')
);
const Copyright = lazy(() => import('../../components/layouts/Copyright'));

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, id } = useMemo(
    () => queryString.parse(location.search),
    [location.search]
  );
  const { sendMailResetPassword, resetPassword } = useContext(AuthContext);
  const [alert, setAlert] = useState(null);
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    token,
    id,
    password: '',
    confirmPassword: '',
  });
  const { password, confirmPassword } = resetPasswordForm;
  const { t } = useTranslation();

  const onChangePasswordForm = useCallback(
    (event) => {
      setResetPasswordForm({
        ...resetPasswordForm,
        [event.target.name]: event.target.value,
      });
    },
    [resetPasswordForm]
  );

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const handleMouseDownPassword = useCallback((event) => {
    event.preventDefault();
  }, []);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      try {
        let registerData;
        if (token === undefined || id === undefined) {
          registerData = await sendMailResetPassword(email);
          setAlert({ type: 'warning', message: 'Please wait' });
          setTimeout(() => setAlert(null), 30000);
        } else {
          if (!password) {
            setAlert({
              type: 'error',
              message: 'password is required',
            });
            setTimeout(() => setAlert(null), 30000);
            return;
          }
          if (!confirmPassword) {
            setAlert({
              type: 'error',
              message: 'confirmPassword is required',
            });
            setTimeout(() => setAlert(null), 30000);
            return;
          }
          if (password.length < 6) {
            setAlert({
              type: 'error',
              message: t('passwordCheck2'),
            });
            setTimeout(() => setAlert(null), 30000);
            return;
          }
          if (password !== confirmPassword) {
            setAlert({ type: 'error', message: t('incorrectPassword') });
            setTimeout(() => setAlert(null), 30000);
            return;
          }
          registerData = await resetPassword(resetPasswordForm);
          setAlert({ type: 'warning', message: 'Please wait' });
          setTimeout(() => setAlert(null), 30000);
        }
        if (!registerData.success) {
          setAlert({ type: 'error', message: registerData.message });
          setTimeout(() => setAlert(null), 30000);
        } else {
          console.log(registerData.message);
          setAlert({ type: 'info', message: registerData.message });
          setTimeout(() => {
            setAlert(null);
            navigate('/login');
          }, 30000);
        }
      } catch (error) {
        console.log(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      confirmPassword,
      email,
      navigate,
      id,
      password,
      resetPassword,
      resetPasswordForm,
      sendMailResetPassword,
      token,
    ]
  );

  let body;
  if (token === undefined || id === undefined) {
    body = (
      <>
        <TextField
          margin="normal"
          required
          fullWidth
          label="Email"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          {t(`resetPassword.sendMail`)}
        </Button>
      </>
    );
  } else {
    body = (
      <>
        <TextField
          margin="normal"
          fullWidth
          name="password"
          label={t(`resetPassword.password`)}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          onChange={onChangePasswordForm}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          value={password}
        />
        <TextField
          margin="normal"
          fullWidth
          name="confirmPassword"
          label={t(`resetPassword.confirmPassword`)}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-confirmPassword"
          onChange={onChangePasswordForm}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          value={confirmPassword}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          {t(`resetPassword.resetBtn`)}
        </Button>
      </>
    );
  }

  return (
    <>
      <Typography component="h1" variant="h5">
        {t(`resetPassword.title`)}
      </Typography>
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{ mt: 1, width: '-webkit-fill-available' }}
      >
        <Suspense fallback={<Loading />}>
          <AlertMessage info={alert} />
        </Suspense>
        {body}
        <Grid container>
          <Grid item>
            <Link to="/login" variant="body2">
              {t(`resetPassword.login`)}
            </Link>
          </Grid>
        </Grid>
        <Suspense fallback={<Loading />}>
          <Copyright sx={{ mt: 5 }} />
        </Suspense>
      </Box>
    </>
  );
};

export default memo(ResetPassword);
